import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppStateContext = createContext();

// Keys for localStorage
const DEEDS_COOLDOWN_KEY = 'legacy_reborn_deeds_cooldown';
const JAIL_STATUS_KEY = 'legacy_reborn_jail_status';
const JAIL_RELEASE_TIME_KEY = 'legacy_reborn_jail_release_time';
const TRAINING_COOLDOWN_KEY = 'training_cooldown';

export const AppStateProvider = ({ children }) => {
  // Initialize with stored values if they exist
  const [deedsCooldown, setDeedsCooldown] = useState(() => {
    const storedCooldown = localStorage.getItem(DEEDS_COOLDOWN_KEY);
    if (storedCooldown) {
      try {
        const cooldownTime = new Date(storedCooldown);
        // If the stored cooldown is in the future, return it
        if (cooldownTime > new Date()) {
          console.log('Restoring deeds cooldown from localStorage:', cooldownTime.toLocaleString());
          return cooldownTime;
        } else {
          console.log('Stored cooldown is expired, clearing');
          localStorage.removeItem(DEEDS_COOLDOWN_KEY);
        }
      } catch (err) {
        console.error('Error parsing stored cooldown:', err);
        localStorage.removeItem(DEEDS_COOLDOWN_KEY);
      }
    }
    return null;
  });
  
  const [inJail, setInJail] = useState(() => {
    const jailStatus = localStorage.getItem(JAIL_STATUS_KEY) === 'true';
    const releaseTimeStr = localStorage.getItem(JAIL_RELEASE_TIME_KEY);
    
    // If there's a release time and it's in the past, they're no longer in jail
    if (releaseTimeStr) {
      try {
        const releaseTime = new Date(releaseTimeStr);
        if (releaseTime <= new Date()) {
          console.log('Jail time expired, clearing jail status');
          localStorage.removeItem(JAIL_STATUS_KEY);
          localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
          return false;
        } else if (jailStatus) {
          console.log('Restoring jail status from localStorage, release at:', releaseTime.toLocaleString());
          return true;
        }
      } catch (err) {
        console.error('Error parsing stored jail release time:', err);
        localStorage.removeItem(JAIL_STATUS_KEY);
        localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
      }
    }
    
    return jailStatus;
  });
  
  const [jailReleaseTime, setJailReleaseTime] = useState(() => {
    const releaseTimeStr = localStorage.getItem(JAIL_RELEASE_TIME_KEY);
    if (releaseTimeStr) {
      const releaseTime = new Date(releaseTimeStr);
      if (releaseTime > new Date()) {
        return releaseTime;
      } else {
        localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
        localStorage.removeItem(JAIL_STATUS_KEY);
      }
    }
    return null;
  });

  const [trainingCooldown, setTrainingCooldown] = useState(() => {
    const storedCooldown = localStorage.getItem(TRAINING_COOLDOWN_KEY);
    if (storedCooldown) {
      try {
        const cooldownDate = new Date(storedCooldown);
        if (cooldownDate > new Date()) {
          console.log('Restoring training cooldown from localStorage:', cooldownDate.toLocaleString());
          return cooldownDate;
        } else {
          localStorage.removeItem(TRAINING_COOLDOWN_KEY);
        }
      } catch (err) {
        console.error('Error parsing stored training cooldown:', err);
        localStorage.removeItem(TRAINING_COOLDOWN_KEY);
      }
    }
    return null;
  });

  // Define these functions first with their own useCallback
  const updateDeedsCooldown = useCallback((cooldownEndTime) => {
    console.log('updateDeedsCooldown called with:', cooldownEndTime);
    
    if (cooldownEndTime) {
      try {
        // Ensure we're working with a Date object
        const cooldownDate = cooldownEndTime instanceof Date ? 
          cooldownEndTime : new Date(cooldownEndTime);
        
        if (cooldownDate > new Date()) { // Only set if future date
          console.log('Setting cooldown to:', cooldownDate.toLocaleString());
          setDeedsCooldown(cooldownDate);
          localStorage.setItem(DEEDS_COOLDOWN_KEY, cooldownDate.toISOString());
          
          // Record when this cooldown was set for conflict resolution
          window.lastCooldownUpdate = Date.now();
        } else {
          console.log('Cooldown date is in the past, clearing:', cooldownDate.toLocaleString());
          setDeedsCooldown(null);
          localStorage.removeItem(DEEDS_COOLDOWN_KEY);
        }
      } catch (err) {
        console.error('Error setting cooldown:', err);
        setDeedsCooldown(null);
        localStorage.removeItem(DEEDS_COOLDOWN_KEY);
      }
    } else {
      console.log('Clearing cooldown entirely');
      setDeedsCooldown(null);
      localStorage.removeItem(DEEDS_COOLDOWN_KEY);
    }
  }, []);  // Empty dependency array since it only uses setState functions

  // Define updateJailStatus with its own useCallback
  const updateJailStatus = useCallback((isInJail, releaseTime = null) => {
    console.log('updateJailStatus called with:', isInJail, releaseTime);
    
    if (isInJail) {
      setInJail(true);
      localStorage.setItem(JAIL_STATUS_KEY, 'true');
      
      if (releaseTime) {
        try {
          const releaseDate = releaseTime instanceof Date ? 
            releaseTime : new Date(releaseTime);
          
          if (releaseDate > new Date()) { // Only set if future date
            console.log('Setting jail release time to:', releaseDate.toLocaleString());
            setJailReleaseTime(releaseDate);
            localStorage.setItem(JAIL_RELEASE_TIME_KEY, releaseDate.toISOString());
          } else {
            console.log('Release time is in the past, clearing jail status');
            setInJail(false);
            setJailReleaseTime(null);
            localStorage.removeItem(JAIL_STATUS_KEY);
            localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
          }
        } catch (err) {
          console.error('Error setting jail release time:', err);
          // Still keep jail status but with no release time
        }
      } else {
        // Clear release time if not provided
        setJailReleaseTime(null);
        localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
      }
    } else {
      // Clear all jail status
      setInJail(false);
      setJailReleaseTime(null);
      localStorage.removeItem(JAIL_STATUS_KEY);
      localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
    }
  }, []);  // Empty dependency array since it only uses setState functions

  const updateTrainingCooldown = useCallback((cooldownEndTime) => {
    console.log('updateTrainingCooldown called with:', cooldownEndTime);
    
    if (cooldownEndTime) {
      try {
        // Ensure we're working with a Date object
        const cooldownDate = cooldownEndTime instanceof Date ? 
          cooldownEndTime : new Date(cooldownEndTime);
        
        if (cooldownDate > new Date()) { // Only set if future date
          console.log('Setting training cooldown to:', cooldownDate.toLocaleString());
          
          // IMPROVEMENT: Force synchronous localStorage update
          localStorage.setItem(TRAINING_COOLDOWN_KEY, cooldownDate.toISOString());
          
          // Update state
          setTrainingCooldown(cooldownDate);
          
          // Set critical global flags for immediate detection
          window.trainingCooldownUpdated = Date.now();
          window.currentTrainingCooldown = cooldownDate.toISOString();
          
          // NEW: Global atomic flag for high-priority detection
          window.__IMMEDIATE_TRAINING_COOLDOWN__ = {
            timestamp: Date.now(),
            cooldownTime: cooldownDate.toISOString()
          };
          
          // NEW: Dispatch event for other components to react immediately
          const event = new CustomEvent('TRAINING_COOLDOWN_CHANGED', {
            detail: {
              cooldown: cooldownDate,
              timestamp: Date.now()
            }
          });
          window.dispatchEvent(event);
          
          return true;
        } else {
          console.log('Training cooldown date is in the past, clearing:', cooldownDate.toLocaleString());
          setTrainingCooldown(null);
          localStorage.removeItem(TRAINING_COOLDOWN_KEY);
        }
      } catch (err) {
        console.error('Error setting training cooldown:', err);
        setTrainingCooldown(null);
        localStorage.removeItem(TRAINING_COOLDOWN_KEY);
      }
    } else {
      console.log('Clearing training cooldown entirely');
      setTrainingCooldown(null);
      localStorage.removeItem(TRAINING_COOLDOWN_KEY);
    }
  }, []);

  // Move refreshCooldowns definition before updateCooldownsFromAPI
  const refreshCooldowns = useCallback(() => {
    console.log('[refreshCooldowns] Checking stored cooldowns...');
    
    // Check cooldown
    const storedCooldown = localStorage.getItem(DEEDS_COOLDOWN_KEY);
    if (storedCooldown) {
      try {
        const cooldownTime = new Date(storedCooldown);
        const now = new Date();
        
        // Debug time values to help diagnose the issue
        console.log('[refreshCooldowns] Found stored cooldown:', {
          cooldownTimeStr: storedCooldown,
          cooldownTime: cooldownTime.toISOString(),
          now: now.toISOString(),
          diff: (cooldownTime.getTime() - now.getTime()) / 1000,
          isInFuture: cooldownTime > now
        });
        
        // Add a larger buffer (5 seconds) to avoid edge cases and flickering
        // This is especially important for page refreshes
        if (cooldownTime.getTime() - now.getTime() > -5000) {
          // If the cooldown is in the future or just recently expired (within 5s)
          console.log('[refreshCooldowns] Setting active cooldown:', cooldownTime.toLocaleString());
          setDeedsCooldown(cooldownTime);
        } else {
          // Only clear if significantly expired
          console.log('[refreshCooldowns] Cooldown significantly expired, clearing state');
          localStorage.removeItem(DEEDS_COOLDOWN_KEY);
          setDeedsCooldown(null);
        }
      } catch (err) {
        console.error('[refreshCooldowns] Error parsing stored cooldown:', err);
        // Don't clear on error, try to keep using any existing cooldown
        if (!deedsCooldown) {
          // Only clear from localStorage if we don't have a cooldown in state
          localStorage.removeItem(DEEDS_COOLDOWN_KEY);
        }
      }
    } else if (!deedsCooldown) {
      // Only log if we don't have a cooldown in state
      console.log('[refreshCooldowns] No stored cooldown found');
    }
    
    // Check training cooldown
    const storedTrainingCooldown = localStorage.getItem(TRAINING_COOLDOWN_KEY);
    if (storedTrainingCooldown) {
      try {
        const cooldownTime = new Date(storedTrainingCooldown);
        const now = new Date();
        
        if (cooldownTime.getTime() - now.getTime() > -5000) {
          console.log('[refreshCooldowns] Setting active training cooldown:', cooldownTime.toLocaleString());
          setTrainingCooldown(cooldownTime);
        } else {
          console.log('[refreshCooldowns] Training cooldown expired, clearing state');
          localStorage.removeItem(TRAINING_COOLDOWN_KEY);
          setTrainingCooldown(null);
        }
      } catch (err) {
        console.error('[refreshCooldowns] Error parsing stored training cooldown:', err);
      }
    }
    
    // Check jail status
    const jailStatus = localStorage.getItem(JAIL_STATUS_KEY) === 'true';
    const releaseTimeStr = localStorage.getItem(JAIL_RELEASE_TIME_KEY);
    
    if (jailStatus && releaseTimeStr) {
      try {
        const releaseTime = new Date(releaseTimeStr);
        const now = new Date();
        
        if (releaseTime > now) {
          console.log('[refreshCooldowns] Setting active jail status:', releaseTime.toLocaleString());
          setInJail(true);
          setJailReleaseTime(releaseTime);
        } else {
          console.log('[refreshCooldowns] Jail time expired, clearing state');
          localStorage.removeItem(JAIL_STATUS_KEY);
          localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
          setInJail(false);
          setJailReleaseTime(null);
        }
      } catch (err) {
        console.error('[refreshCooldowns] Error parsing jail release time:', err);
      }
    }
  }, [deedsCooldown]); // Only depend on deedsCooldown for condition check

  // Now define updateCooldownsFromAPI after refreshCooldowns is defined
  const updateCooldownsFromAPI = useCallback((apiData) => {
    if (!apiData) return false;
    console.log('[AppState] Updating cooldowns from API data', apiData);
    
    // Handle force refresh case
    if (apiData.force && apiData.refresh) {
      console.log('[AppState] Forcing cooldown refresh from localStorage');
      refreshCooldowns();
      return true;
    }
    
    // Record the current cooldown state before potentially updating
    const hadCooldownBefore = !!deedsCooldown;
    
    // Only update cooldown if the API explicitly provides a cooldown value
    if (apiData.deedsCooldown !== undefined) {
      // If API provides a date string for cooldown
      if (apiData.deedsCooldown) {
        try {
          const apiCooldownDate = new Date(apiData.deedsCooldown);
          const now = new Date();
          
          // Only update if API cooldown is in the future (still active)
          if (apiCooldownDate > now) {
            console.log('[AppState] Setting cooldown from API:', apiCooldownDate);
            updateDeedsCooldown(apiCooldownDate);
          } else {
            console.log('[AppState] API cooldown exists but is in the past, not updating');
            // IMPORTANT: Don't clear existing cooldown just because API has expired one
            // Only clear if we're certain the API is explicitly clearing it
            if (apiData.deedsCooldown === null) {
              console.log('[AppState] API explicitly cleared cooldown');
              updateDeedsCooldown(null);
            }
          }
        } catch (err) {
          console.error('[AppState] Error parsing API cooldown date:', err);
          // Don't update on error
        }
      } else if (apiData.deedsCooldown === null && hadCooldownBefore) {
        // API explicitly indicates no cooldown and we had one before
        console.log('[AppState] API explicitly indicates no cooldown, clearing');
        updateDeedsCooldown(null);
      }
    }
    
    // Similarly protect the jail status update
    if (apiData.inJail !== undefined) {
      updateJailStatus(apiData.inJail, apiData.jailReleaseTime || null);
    }

    // More explicitly handle training cooldowns
    if (apiData.trainingCooldown !== undefined) {
      try {
        if (apiData.trainingCooldown) {
          const cooldownDate = new Date(apiData.trainingCooldown);
          const now = new Date();
          
          // Only update if API cooldown is in the future (still active)
          if (cooldownDate > now) {
            console.log('[AppState] Setting training cooldown from API:', cooldownDate);
            updateTrainingCooldown(cooldownDate);
          } else if (apiData.trainingCooldown === null) {
            console.log('[AppState] API explicitly cleared training cooldown');
            updateTrainingCooldown(null);
          }
        } else if (apiData.trainingCooldown === null) {
          console.log('[AppState] API explicitly cleared training cooldown');
          updateTrainingCooldown(null);
        }
      } catch (err) {
        console.error('[AppState] Error processing training cooldown:', err);
      }
    }
    
    return true;
  }, [deedsCooldown, updateDeedsCooldown, updateJailStatus, updateTrainingCooldown, refreshCooldowns]);

  // Check cooldowns and jail status periodically to update in real-time
  useEffect(() => {
    const checkStatus = () => {
      const now = new Date();
      
      // Check cooldown
      if (deedsCooldown && now >= deedsCooldown) {
        console.log('Cooldown expired, clearing');
        setDeedsCooldown(null);
        localStorage.removeItem(DEEDS_COOLDOWN_KEY);
      }
      
      // Check jail status
      if (inJail && jailReleaseTime && now >= jailReleaseTime) {
        console.log('Jail time expired, releasing');
        setInJail(false);
        setJailReleaseTime(null);
        localStorage.removeItem(JAIL_STATUS_KEY);
        localStorage.removeItem(JAIL_RELEASE_TIME_KEY);
      }

      // Check training cooldown
      if (trainingCooldown && now >= trainingCooldown) {
        console.log('Training cooldown expired, clearing');
        setTrainingCooldown(null);
        localStorage.removeItem(TRAINING_COOLDOWN_KEY);
      }
    };
    
    // Check every second for status updates
    const intervalId = setInterval(checkStatus, 1000);
    return () => clearInterval(intervalId);
  }, [deedsCooldown, inJail, jailReleaseTime, trainingCooldown]);

  // Register the update function globally so UserContext can access it
  useEffect(() => {
    window.updateCooldownsFromAPI = updateCooldownsFromAPI;
    return () => {
      delete window.updateCooldownsFromAPI;
    };
  }, [updateCooldownsFromAPI]);

  // Check if deeds are available (no cooldown)
  const areTheDeedsAvailable = () => {
    // If there's no cooldown, deeds are available
    if (!deedsCooldown) return true;
    
    // Compare dates properly - need to ensure we compare correctly
    const now = new Date();
    // Add small buffer to prevent edge cases with millisecond timing
    const available = now.getTime() > (deedsCooldown.getTime() + 100);
    
    // Debug log
    console.log('[AppState] Deeds available check:', { 
      available, 
      now: now.toLocaleString(), 
      cooldown: deedsCooldown?.toLocaleString() || 'None',
      timeDiff: deedsCooldown ? (deedsCooldown.getTime() - now.getTime()) / 1000 + 's' : 'N/A'
    });
    
    return available;
  };

  const isTrainingAvailable = useCallback(() => {
    if (!trainingCooldown) return true;
    
    const now = new Date();
    return now.getTime() > (trainingCooldown.getTime() + 100);
  }, [trainingCooldown]);

  return (
    <AppStateContext.Provider 
      value={{ 
        deedsCooldown, 
        inJail,
        jailReleaseTime,
        trainingCooldown,
        updateDeedsCooldown, 
        updateJailStatus,
        updateTrainingCooldown,
        areTheDeedsAvailable,
        isTrainingAvailable,
        updateCooldownsFromAPI,
        refreshCooldowns // Add this new function
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};